import { useState, useEffect } from "react";
import { X, ArrowRight } from "lucide-react";

const WA_NUMBER = "447426821449";
const messages = [
  {
    label: "Lower my card machine costs",
    text: "Hi, I'd like to enquire how I can lower my card machine costs.",
  },
  {
    label: "Get a free quote",
    text: "Hi, I'd like to get a free quote from Fenco Digital.",
  },
  {
    label: "Something else",
    text: "Hi, I'd like to chat about Fenco Digital.",
  },
];

export default function FloatingWhatsApp() {
  const [open, setOpen] = useState(false);
  const [showHint, setShowHint] = useState(false);

  // Show a small hint bubble after 4s on first load (per session)
  useEffect(() => {
    if (sessionStorage.getItem("fenco-wa-hint")) return;
    const t = setTimeout(() => setShowHint(true), 4000);
    const t2 = setTimeout(() => {
      setShowHint(false);
      sessionStorage.setItem("fenco-wa-hint", "1");
    }, 12000);
    return () => {
      clearTimeout(t);
      clearTimeout(t2);
    };
  }, []);

  const dismissHint = () => {
    setShowHint(false);
    sessionStorage.setItem("fenco-wa-hint", "1");
  };

  return (
    <div
      className="fixed bottom-6 left-6 z-[60] flex flex-col items-start gap-3"
      data-testid="floating-whatsapp"
    >
      {/* Expanded panel */}
      {open && (
        <div
          className="bg-white rounded-2xl shadow-2xl border border-gray-100 w-[300px] p-5 mb-1 animate-in"
          data-testid="floating-whatsapp-panel"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#25D366]">WhatsApp</p>
              <p className="font-display text-base font-bold text-gray-900 mt-1">Chat with the team</p>
              <p className="text-xs text-gray-500 mt-1">Pick a topic to start.</p>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-gray-400 hover:text-gray-700 p-1 -mr-1"
              data-testid="floating-whatsapp-close"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>

          <div className="mt-4 space-y-2">
            {messages.map((m, i) => (
              <a
                key={m.label}
                href={`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(m.text)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`floating-whatsapp-msg-${i}`}
                onClick={() => setOpen(false)}
                className="flex items-center justify-between gap-3 px-3.5 py-3 bg-[#F8F9FA] hover:bg-[#25D366] hover:text-white rounded-xl text-sm font-semibold text-gray-900 transition group"
              >
                <span>{m.label}</span>
                <ArrowRight size={14} className="text-gray-400 group-hover:text-white transition" />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Hint bubble */}
      {!open && showHint && (
        <button
          onClick={() => {
            setOpen(true);
            dismissHint();
          }}
          data-testid="floating-whatsapp-hint"
          className="bg-white rounded-2xl shadow-xl border border-gray-100 px-4 py-2.5 text-sm font-semibold text-gray-900 mb-1 hover:-translate-y-0.5 transition-all relative pr-9"
        >
          <span>👋 Quick question? Message us.</span>
          <span
            onClick={(e) => {
              e.stopPropagation();
              dismissHint();
            }}
            className="absolute top-1 right-1 text-gray-400 hover:text-gray-700 p-1 cursor-pointer"
          >
            <X size={12} />
          </span>
        </button>
      )}

      {/* FAB */}
      <button
        onClick={() => {
          setOpen(!open);
          dismissHint();
        }}
        aria-label="Open WhatsApp chat"
        data-testid="floating-whatsapp-toggle"
        className="relative w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#1ebe57] shadow-2xl shadow-[#25D366]/40 flex items-center justify-center transition-all hover:-translate-y-1"
      >
        {!open && (
          <span className="absolute inset-0 rounded-full bg-[#25D366] opacity-50 animate-ping" />
        )}
        <svg viewBox="0 0 32 32" className="w-7 h-7 relative z-10" fill="white" aria-hidden="true">
          <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.032 1.318-1.06 2.264v.114c-.015.99.472 1.977 1.017 2.78 1.23 1.82 2.35 3.41 4.345 4.526.616.343 1.318.658 2.005.787.34.072.74.13 1.088.13 1.075 0 2.47-.747 2.815-1.834.13-.388.158-.83.158-1.246 0-.187-.043-.358-.085-.53-.13-.41-1.66-1.116-2.005-1.116l-.03.026Zm-3.193 9.292a10.91 10.91 0 0 1-5.41-1.461l-3.873 1.232 1.262-3.764a10.92 10.92 0 0 1-2.115-6.467c0-6.08 4.945-11.026 11.026-11.026a10.95 10.95 0 0 1 7.79 3.225 10.97 10.97 0 0 1 3.23 7.795c-.002 6.08-4.945 11.025-11.026 11.025h.115l.001.441Zm0-23.927C8.85 2.57 3.014 8.405 3.014 15.474c0 2.265.578 4.47 1.706 6.404L2.013 28.926l7.18-2.276a13.012 13.012 0 0 0 6.226 1.578h.005c7.067 0 12.902-5.836 12.902-12.902 0-3.45-1.346-6.69-3.788-9.135a12.838 12.838 0 0 0-9.13-3.79l.001.169Z"/>
        </svg>
      </button>
    </div>
  );
}
